'use client'

import { useRef } from 'react'
import { motion, useInView } from 'motion/react'
import Image from 'next/image'

const About = () => {
	const ref = useRef(null)
	const isInView = useInView(ref, { once: false, amount: 0.3 })

	const containerVariants = {
		hidden: { opacity: 0 },
		visible: {
			opacity: 1,
			transition: {
				staggerChildren: 0.15
			}
		}
	} as const

	const itemVariants = {
		hidden: { opacity: 0, y: 30 },
		visible: {
			opacity: 1,
			y: 0,
			transition: {
				duration: 0.6,
				ease: [0.22, 1, 0.36, 1] as const
			}
		}
	} as const

	const traits = ['Creative', 'Detail-oriented', 'Problem Solver', 'Team Player', 'AI Engineer']

	return (
		<section id="about" className="py-24 md:py-32">
			<div className="section-container">
				<motion.div
					ref={ref}
					variants={containerVariants}
					initial="hidden"
					animate={isInView ? 'visible' : 'hidden'}
					className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center"
				>
					{/* Image */}
					<motion.div variants={itemVariants} className="relative">
						<div className="relative aspect-4/4 max-h-[400px] rounded-3xl overflow-hidden glass-card p-1.5">
							<div className="absolute inset-0 bg-linear-to-tr from-green-500/10 to-emerald-500/10 rounded-3xl" />
							<div className="relative w-full h-full rounded-2xl overflow-hidden">
								<Image
									src="/imgs/da4ndo.png"
									alt="Daniel Varga"
									fill
									className="object-cover object-top"
									sizes="(max-width: 1024px) 100vw, 50vw"
									priority
								/>
							</div>
						</div>
					</motion.div>

					{/* Content */}
					<div>
						<motion.h2 variants={itemVariants} className="text-3xl md:text-4xl font-bold mb-6">
							About <span className="text-gradient">Me</span>
						</motion.h2>

						<motion.p variants={itemVariants} className="text-lg text-gray-600 dark:text-gray-400 mb-8 leading-relaxed">
							Crafting engaging and user-friendly applications is what gets me going.
							I&apos;m a firm believer in the power of collaboration and enjoy working with
							talented individuals to bring ideas to life.
						</motion.p>

						<motion.div variants={itemVariants} className="space-y-3 mb-8">
							{[
								{ label: 'Profession', value: 'Software & AI Engineer' },
								{ label: 'Business', value: 'Varga Dániel e.v.' },
								{ label: 'Location', value: 'Hungary' },
								{ label: 'Education', value: 'Software Development and Testing' },
								{ label: 'Status', value: 'Student, working part-time' },
								{ label: 'Languages', value: 'English, Hungarian, German' }
							].map((item) => (
								<div key={item.label} className="flex items-center text-sm">
									<span className="font-medium w-28 text-gray-500 dark:text-gray-400">{item.label}</span>
									<span className="text-gray-800 dark:text-gray-200">{item.value}</span>
								</div>
							))}
						</motion.div>

						<motion.div variants={itemVariants} className="flex flex-wrap gap-2">
							{traits.map((trait) => (
								<span
									key={trait}
									className={`px-4 py-2 rounded-full text-sm font-medium ${
										trait === 'AI Engineer'
											? 'animate-border-glow bg-white/90 dark:bg-[#1a1a1a] text-gray-800 dark:text-gray-200'
											: 'glass-card text-gray-700 dark:text-gray-300'
									}`}
								>
									{trait}
								</span>
							))}
						</motion.div>
					</div>
				</motion.div>
			</div>
		</section>
	)
}

export default About
